import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  DocumentChartBarIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { ticketsAPI, usersAPI } from '../api/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import DataTable from '../components/ui/DataTable';

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const downloadRowsAsExcel = (rows, dateFrom, dateTo) => {
  const sheetRows = (rows || []).map((r) => ({
    'Ticket ID': r.id,
    'Başlık': r.title,
    'Proje': r.project_name,
    'Proje Kodu': r.project_code,
    'Oluşturan': r.created_by,
    'İş Yapan (Atanan)': r.assigned_to,
    'Durum': r.status,
    'Öncelik': r.priority,
    'Otel': r.hotel,
    'Konu': r.topic,
    'Tip': r.ticket_type,
    'Bitiş Tarihi': r.due_date,
    'Oluşturma': r.created_at,
    'Güncelleme': r.updated_at,
    'Açıklama': r.description,
  }));
  const ws = XLSX.utils.json_to_sheet(sheetRows.length ? sheetRows : [{ 'Ticket ID': '' }]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'İş Raporu');
  XLSX.writeFile(wb, `is_raporu_${dateFrom || 'baslangic'}_${dateTo || 'bitis'}.xlsx`);
};

const presets = [
  {
    id: 'today',
    label: 'Bugün',
    range: () => {
      const today = toIsoDate(new Date());
      return { date_from: today, date_to: today };
    },
  },
  {
    id: 'week',
    label: 'Bu Hafta',
    range: () => {
      const now = new Date();
      return { date_from: toIsoDate(startOfWeek(now)), date_to: toIsoDate(now) };
    },
  },
  {
    id: 'month',
    label: 'Bu Ay',
    range: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      return { date_from: toIsoDate(from), date_to: toIsoDate(now) };
    },
  },
];

const ReportsPage = () => {
  const { canAccessWorkReport } = useAuth();
  const weekRange = presets.find((p) => p.id === 'week').range();

  const [dateFrom, setDateFrom] = useState(weekRange.date_from);
  const [dateTo, setDateTo] = useState(weekRange.date_to);
  const [activePreset, setActivePreset] = useState('week');
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await usersAPI.getUsers();
        const list = Array.isArray(res) ? res : (res?.data || res?.results || []);
        if (!cancelled) {
          setUsers(
            list
              .filter((u) => u?.is_active !== false)
              .map((u) => ({
                id: u.id,
                name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || `User #${u.id}`,
              }))
              .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
          );
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLocaleLowerCase('tr-TR');
    if (!q) return users;
    return users.filter((u) => u.name.toLocaleLowerCase('tr-TR').includes(q));
  }, [users, userSearch]);

  const applyPreset = (preset) => {
    const range = preset.range();
    setDateFrom(range.date_from);
    setDateTo(range.date_to);
    setActivePreset(preset.id);
  };

  const toggleUser = (id) => {
    setSelectedUserIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  const runReport = useCallback(async () => {
    if (!dateFrom || !dateTo) {
      setError('Başlangıç ve bitiş tarihi seçin.');
      return;
    }
    if (dateFrom > dateTo) {
      setError('Başlangıç tarihi bitiş tarihinden sonra olamaz.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await ticketsAPI.getWorkReport({
        date_from: dateFrom,
        date_to: dateTo,
        assigned_to: selectedUserIds,
      });
      const results = res?.data?.results ?? res?.results ?? [];
      setRows(Array.isArray(results) ? results : []);
      setHasRun(true);
    } catch (e) {
      const msg = e.message || 'Rapor oluşturulamadı.';
      setError(
        msg.includes('404')
          ? `${msg} Backend'de /api/tickets/work-report/ endpoint'i bulunamadı. Django sunucusunu yeniden başlatın veya yeni kodu deploy edin.`
          : msg
      );
      setRows([]);
      setHasRun(true);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedUserIds]);

  const exportExcel = async () => {
    if (!dateFrom || !dateTo) {
      setError('Başlangıç ve bitiş tarihi seçin.');
      return;
    }
    setExporting(true);
    setError(null);
    try {
      // Rapor başarıyla getirildiyse ekrandaki veriden Excel üret
      if (hasRun && !error) {
        downloadRowsAsExcel(rows, dateFrom, dateTo);
        return;
      }
      // Sonuç yoksa API'den Excel indir
      await ticketsAPI.downloadWorkReportExcel({
        date_from: dateFrom,
        date_to: dateTo,
        assigned_to: selectedUserIds,
      });
    } catch (e) {
      // API Excel başarısızsa önce raporu çekip client-side üret
      try {
        const res = await ticketsAPI.getWorkReport({
          date_from: dateFrom,
          date_to: dateTo,
          assigned_to: selectedUserIds,
        });
        const results = res?.data?.results ?? res?.results ?? [];
        const list = Array.isArray(results) ? results : [];
        setRows(list);
        setHasRun(true);
        downloadRowsAsExcel(list, dateFrom, dateTo);
      } catch (err) {
        const msg = err.message || e.message || 'Excel indirilemedi.';
        setError(
          msg.includes('404')
            ? `${msg} Backend endpoint bulunamadı. Django'yu yeniden başlatın.`
            : msg
        );
      }
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo(() => [
    { accessorKey: 'id', header: 'ID', size: 70 },
    { accessorKey: 'title', header: 'Başlık', size: 220 },
    { accessorKey: 'project_name', header: 'Proje', size: 160 },
    { accessorKey: 'assigned_to', header: 'İş Yapan', size: 140 },
    { accessorKey: 'created_by', header: 'Oluşturan', size: 140 },
    { accessorKey: 'status', header: 'Durum', size: 110 },
    { accessorKey: 'priority', header: 'Öncelik', size: 100 },
    { accessorKey: 'created_at', header: 'Oluşturma', size: 130 },
  ], []);

  if (!canAccessWorkReport()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-5 p-4 md:p-6">
      <div className="rounded-2xl border border-teal-100 bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-5 text-white shadow-sm dark:border-teal-900">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-1 flex items-center gap-2 text-teal-50">
              <DocumentChartBarIcon className="h-5 w-5" />
              <span className="text-xs font-semibold uppercase tracking-wide">Rapor Alma</span>
            </div>
            <h1 className="text-2xl font-bold">İş Yapanlar Raporu</h1>
            <p className="mt-1 max-w-2xl text-sm text-teal-50">
              Seçtiğiniz tarih aralığında oluşturulan talepleri, atanan kişilere göre listeleyin ve Excel olarak indirin.
            </p>
          </div>
          <Button
            type="button"
            onClick={exportExcel}
            disabled={exporting || !dateFrom || !dateTo}
            className="inline-flex items-center gap-2 bg-white text-teal-800 hover:bg-teal-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {exporting ? 'İndiriliyor…' : 'Excel İndir'}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
          <CalendarDaysIcon className="h-5 w-5 text-teal-600" />
          Tarih Aralığı
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {presets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => applyPreset(preset)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                activePreset === preset.id
                  ? 'bg-teal-600 text-white'
                  : 'border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
              }`}
            >
              {preset.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setActivePreset('custom')}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
              activePreset === 'custom'
                ? 'bg-teal-600 text-white'
                : 'border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
            }`}
          >
            Özel
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label className="mb-1 text-xs">Başlangıç</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setActivePreset('custom');
              }}
            />
          </div>
          <div>
            <Label className="mb-1 text-xs">Bitiş</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setActivePreset('custom');
              }}
            />
          </div>
          <div className="flex items-end gap-2 sm:col-span-2">
            <Button
              type="button"
              onClick={runReport}
              disabled={loading}
              className="bg-teal-600 text-white hover:bg-teal-700"
            >
              {loading ? 'Hazırlanıyor…' : 'Raporu Getir'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={exportExcel}
              disabled={exporting || loading || !dateFrom || !dateTo}
              className="inline-flex items-center gap-2 border-teal-600 text-teal-700 hover:bg-teal-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {exporting ? 'İndiriliyor…' : 'Excel İndir'}
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
            <UsersIcon className="h-5 w-5 text-teal-600" />
            İş Yapanlar (Atanan)
            <span className="text-xs font-normal text-gray-500">
              {selectedUserIds.length ? `${selectedUserIds.length} kişi seçili` : 'Tümü'}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="text-xs"
              onClick={() => setSelectedUserIds([])}
            >
              Tümünü Seç (filtre yok)
            </Button>
          </div>
        </div>

        <Input
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          placeholder="Kişi ara..."
          className="mb-3"
        />

        <div className="max-h-44 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/50">
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
            {filteredUsers.map((user) => {
              const checked = selectedUserIds.includes(user.id);
              return (
                <label
                  key={user.id}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm ${
                    checked
                      ? 'bg-teal-50 text-teal-900 dark:bg-teal-900/30 dark:text-teal-100'
                      : 'text-gray-700 hover:bg-white dark:text-gray-200 dark:hover:bg-gray-800'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleUser(user.id)}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="truncate">{user.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            Sonuçlar
            {hasRun ? (
              <span className="ml-2 text-xs font-normal text-gray-500">{rows.length} kayıt</span>
            ) : null}
          </h2>
          {hasRun && !error && (
            <Button
              type="button"
              variant="outline"
              onClick={exportExcel}
              disabled={exporting}
              className="inline-flex items-center gap-2 text-xs"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Excel İndir
            </Button>
          )}
        </div>

        {!hasRun ? (
          <div className="px-4 py-12 text-center text-sm text-gray-500">
            Tarih aralığını seçip “Raporu Getir”e basın. Excel için doğrudan “Excel İndir” kullanabilirsiniz.
          </div>
        ) : loading ? (
          <div className="px-4 py-12 text-center text-sm text-gray-500">Rapor hazırlanıyor…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-gray-500">
            Seçilen kriterlere uygun kayıt bulunamadı.
          </div>
        ) : (
          <div className="p-2">
            <DataTable data={rows} columns={columns} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
